import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const beautySubmissionSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  age: z.string().refine(
    (val) => {
      const age = parseInt(val);
      return !isNaN(age) && age >= 18;
    },
    { message: 'Must be 18 or older' }
  ),
  city: z.string().min(2, 'City is required'),
  profession: z.string().min(2, 'Profession is required'),
  hobbies: z.string().min(10, 'Please tell us about your hobbies'),
  relationshipStatus: z.enum(['single', 'married', 'partnered'], {
    required_error: 'Please select your relationship status',
  }),
  bathingSuitPhoto: z.any().refine((files) => files?.length > 0, 'Bathing suit photo is required'),
  professionalPhoto: z.any().refine((files) => files?.length > 0, 'Professional attire photo is required'),
});

type BeautySubmissionFormData = z.infer<typeof beautySubmissionSchema>;

export function BeautyOfWeekForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<BeautySubmissionFormData>({
    resolver: zodResolver(beautySubmissionSchema),
    defaultValues: {
      name: '',
      age: '',
      city: '',
      profession: '',
      hobbies: '',
      relationshipStatus: undefined,
    },
  });

  const onSubmit = async (data: BeautySubmissionFormData) => {
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('age', data.age);
      formData.append('city', data.city);
      formData.append('profession', data.profession);
      formData.append('hobbies', data.hobbies);
      formData.append('relationshipStatus', data.relationshipStatus);
      
      // Add files
      if (data.bathingSuitPhoto && data.bathingSuitPhoto[0]) {
        formData.append('bathingSuitPhoto', data.bathingSuitPhoto[0]);
      }
      if (data.professionalPhoto && data.professionalPhoto[0]) {
        formData.append('professionalPhoto', data.professionalPhoto[0]);
      }

      const response = await fetch('/api/beauty-of-week', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      toast({
        title: 'Success!',
        description: 'Your Beauty of the Week submission has been received. Thank you!',
      });
      
      form.reset();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit your application. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-8 shadow-lg border-2 border-black">
      <h3 className="text-3xl font-bold text-center mb-6 text-black">💫 Beauty Queen Application 💫</h3>
      <p className="text-center text-gray-700 mb-8 font-semibold text-lg">
        <span className="text-black font-bold">✨ Ready to be featured? Let's make it happen! ✨</span>
      </p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your full name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="age"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Age</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="Must be 18 or older" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input placeholder="Your city" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profession"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Profession</FormLabel>
                <FormControl>
                  <Input placeholder="What do you do?" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="hobbies"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Hobbies & Interests</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Tell us about your hobbies and interests" 
                    className="min-h-[100px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="relationshipStatus"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Relationship Status</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your relationship status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="partnered">Partnered</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bathingSuitPhoto"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormItem>
                <FormLabel>Current Snapshot in Bathing Suit</FormLabel>
                <FormControl>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onChange(e.target.files)}
                    {...rest}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="professionalPhoto"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormItem>
                <FormLabel>Photo in Professional Attire</FormLabel>
                <FormControl>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onChange(e.target.files)}
                    {...rest}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full bg-black hover:bg-gray-800 text-white border-2 border-gold font-bold py-3 text-lg rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}